import {
  AssignmentTurnedInOutlined as AssignmentIcon,
  BookOutlined as BookIcon,
  HomeOutlined as HomeIcon,
} from "@mui/icons-material";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

const StudentSideBar = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const dir = i18n?.language === "ar" ? "rtl" : "ltr";

  const items = useMemo(
    () => [
      {
        key: "home",
        to: "/",
        icon: <HomeIcon />,
        label: { en: "Home", ar: "الرئيسية" },
      },
      {
        key: "enrolled-courses",
        to: "/dashboard",
        icon: <BookIcon />,
        label: { en: "Enrolled courses", de: "Meine Kurse", ar: "الدورات المسجل بها" },
      },
      {
        key: "submissions",
        to: "/dashboard/student/submissions",
        icon: <AssignmentIcon />,
        label: { en: "Submissions", de: "Bewerbungen", ar: "التقديمات" },
      },
    ],
    []
  );

  const language = i18n?.language?.split("-")[0];
  const labelFor = (item) => item.label[language] || item.label.en;

  return (
    <Box
      component="nav"
      sx={{
        direction: dir,
        position: "fixed",
        top: 0,
        [dir === "rtl" ? "right" : "left"]: 0,
        height: "100vh",
        width: "var(--sidebar-width)",
        bgcolor: "#ffffff",
        borderRight: dir === "ltr" ? "1px solid #e6eaea" : "none",
        borderLeft: dir === "rtl" ? "1px solid #e6eaea" : "none",
        display: "flex",
        flexDirection: "column",
        zIndex: 1100,
      }}
    >
      {/* Section title */}
      <Typography
        variant="caption"
        sx={{
          color: "#7b8b86",
          px: 2,
          pt: 1,
          pb: 0.5,
          textAlign: dir === "rtl" ? "right" : "left",
          fontWeight: "bold",
        }}
      >
        {i18n?.language === "ar" ? "القائمة الأساسية" : "Main Menu"}
      </Typography>

      {/* Navigation items */}
      <List sx={{ p: 0 }}>
        {items.map((item) => {
          const active = location.pathname === item.to;
          return (
            <ListItem key={item.key} disablePadding sx={{ display: "block" }}>
              {item.to ? (
                <Link
                  to={item.to}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <ListItemButton
                    selected={active}
                    sx={{
                      px: 2,
                      py: 1,
                      minHeight: 44,
                      borderRadius: 1,
                      mx: 1,
                      my: 0.5,
                      flexDirection: dir === "rtl" ? "row-reverse" : "row",
                      "&.Mui-selected": { bgcolor: "#eef3f3" },
                      "&:hover": { bgcolor: "#f6f9f9" },
                    }}
                  >
                    <ListItemText
                      primary={labelFor(item)}
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: 600,
                          textAlign: dir === "rtl" ? "right" : "left",
                        },
                      }}
                    />
                    <ListItemIcon sx={{ minWidth: 36, color: "#1f3d37" }}>
                      {item.icon}
                    </ListItemIcon>
                  </ListItemButton>
                </Link>
              ) : (
                <Tooltip
                  sx={{ height: "12px", width: "12px" }}
                  title={t("comingSoon")}
                  arrow
                >
                  <Box
                    sx={{
                      display: "block",
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    <ListItemButton
                      sx={{
                        px: 2,
                        py: 1,
                        minHeight: 44,
                        borderRadius: 1,
                        mx: 1,
                        my: 0.5,
                        flexDirection: dir === "rtl" ? "row-reverse" : "row",
                        "&:hover": { bgcolor: "#f6f9f9" },
                      }}
                    >
                      <ListItemText
                        primary={labelFor(item)}
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: 600,
                            textAlign: dir === "rtl" ? "right" : "left",
                          },
                        }}
                      />
                      <ListItemIcon sx={{ minWidth: 36, color: "#1f3d37" }}>
                        {item.icon}
                      </ListItemIcon>
                    </ListItemButton>
                  </Box>
                </Tooltip>
              )}
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ flexGrow: 1 }} />
    </Box>
  );
};

export default StudentSideBar;
